import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';

type PaymentDetailProps = {
  paymentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
};

// Assuming same type as List but might have more fields
type PaymentDetailData = {
  id: string;
  amount: number;
  method: string;
  status: 'PENDING' | 'PROCESSING' | 'PAID' | 'FAILED' | 'REFUNDED' | 'CANCELLED';
  paidAt?: string;
  transactionId?: string;
  providerResponse?: string;
  createdAt: string;
  application: {
    receiptNumber?: string;
    applicant: {
      firstName: string;
      lastName: string;
    };
    offering: {
      name: string;
    };
  };
};

export const PaymentDetail = ({ paymentId, open, onOpenChange, onUpdate }: PaymentDetailProps) => {
  const [payment, setPayment] = useState<PaymentDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (open && paymentId) {
      setLoading(true);
      fetch(`/api/payments/${paymentId}`)
        .then((res) => res.json())
        .then((data) => setPayment(data as PaymentDetailData))
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [paymentId, open]);

  const handleUpdateStatus = async (status: string) => {
    if (!payment) return;
    setUpdating(true);
    try {
      const res = await fetch(`/api/payments/${payment.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update');
      onUpdate();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'default';
      case 'PENDING':
        return 'outline';
      default:
        return 'destructive';
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Payment Details</DialogTitle>
          <DialogDescription>Transaction ID: {payment?.id}</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}

        {!loading && !payment && <div className="text-center text-red-500">Failed to load details</div>}

        {!loading && payment && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Status</div>
                <div className="mt-1">
                  <Badge variant={getStatusBadgeVariant(payment.status)}>{payment.status}</Badge>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Method</div>
                <div className="mt-1 font-medium">{payment.method || '-'}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Amount</div>
                <div className="mt-1 font-medium text-lg">¥{payment.amount.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Date Created</div>
                <div className="mt-1 font-medium">{new Date(payment.createdAt).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Paid At</div>
                <div className="mt-1 font-medium text-sm">
                  {payment.paidAt ? new Date(payment.paidAt).toLocaleString() : '-'}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Transaction Ref</div>
                <div className="mt-1 font-medium text-sm font-mono">{payment.transactionId || '-'}</div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="font-medium mb-2">Application Info</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Receipt #: {payment.application?.receiptNumber || '-'}</div>
                <div>Exam: {payment.application?.offering?.name}</div>
                <div>
                  Applicant: {payment.application?.applicant?.lastName} {payment.application?.applicant?.firstName}
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {payment?.status === 'PENDING' && (
            <Button onClick={() => handleUpdateStatus('PAID')} disabled={updating}>
              {updating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Mark as Paid
            </Button>
          )}
          {payment?.status === 'PAID' && (
            <Button variant="destructive" onClick={() => handleUpdateStatus('REFUNDED')} disabled={updating}>
              refund (Manual)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
